import Forum from "messaging/forums/forum";

const chatTypeLabels: Record<string, string> = {
  NetworkConnection: "Network connection",
  UserNetworkConnection: "Network connection",
  "Networked.Group": "Network group",
  "Marketplace.Individual": "Individual",
  "Marketplace.Company": "Company",
  "Marketplace.Team": "Team",
  "Work.Proposal": "Proposal",
  "Work.Agreement": "Work agreement",
  userCreatedChat: "Conversation",
};

const networkConnectionEntityClasses = new Set(["NetworkConnection", "UserNetworkConnection"]);

export function getForumChatTypeLabel(forum: Forum): string {
  const entityClass = forum.topic?.entityClass?.value;
  if (!entityClass) return "Conversation";

  return chatTypeLabels[entityClass] ?? humanizeChatType(entityClass);
}

/**
 * What a conversation row says under its title: what the conversation is about, and — for a topic
 * that has one — the stage that topic is at, so a proposal thread says where its proposal stands
 * without the reader opening it (#5129).
 */
export function getForumChatSubtitle(
  forum: Forum,
  entityName?: string,
  currentUserName?: string,
  entityStatus?: string,
): string {
  const segments = [getForumChatTypeLabel(forum)];

  if (entityName) {
    // The name earns its place only when the row is not already titled with it.
    const title = getForumChatTitle(forum, currentUserName);
    if (title.trim().toLocaleLowerCase() !== entityName.trim().toLocaleLowerCase()) segments.push(entityName);
  }

  if (entityStatus) segments.push(entityStatus);

  return segments.join(" · ");
}

export function getForumChatTitle(forum: Forum, currentUserName?: string): string {
  const entityClass = forum.topic?.entityClass?.value;
  let title = cleanGeneratedForumTitle(forum.name.toString().trim(), entityClass);

  if (networkConnectionEntityClasses.has(entityClass ?? "")) {
    title = title.replace(/\s+connection chat\s*$/i, "");
  }

  const separator = networkConnectionEntityClasses.has(entityClass ?? "") ? /\s*<=>\s*|\s*,\s*/ : /\s*,\s*/;
  const participants = title.split(separator).map((participant) => participant.trim()).filter(Boolean);
  const filteredParticipants = participants.filter((participant) => participant !== currentUserName);

  return filteredParticipants.join(", ") || title;
}

function cleanGeneratedForumTitle(title: string, entityClass?: string): string {
  if (entityClass === "Work.Proposal") {
    const proposalCommentThreadMatch = title.match(
      /^Proposal\s+(?:clientReviewers|vendorReviewers|allReviewers)\s+(.+?)\s+comment thread$/i,
    );
    if (proposalCommentThreadMatch?.[1]) return proposalCommentThreadMatch[1].trim();

    return title
      .replace(/^(?:Proposal|Project):\s*/i, "")
      .replace(/\s+general\s+comment thread\s*$/i, "")
      .replace(/\s+comment thread\s*$/i, "")
      .trim();
  }

  if (entityClass === "Marketplace.Company") {
    return title.replace(/^Contact with\s+/i, "").trim();
  }

  if (entityClass === "Work.Agreement") {
    return title.replace(/^Work Agreement:\s*/i, "").replace(/^Agreement:\s*/i, "").trim();
  }

  return title;
}

function humanizeChatType(entityClass: string): string {
  const typeName = entityClass.split(".").at(-1) ?? entityClass;
  const humanized = typeName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return humanized ? humanized.charAt(0).toUpperCase() + humanized.slice(1) : "Conversation";
}
